import React, { MouseEvent } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { themes } from 'kb-shared';
import { LeftBracket, RightBracket } from 'kb-web-shared';

/**
 * Shared primitives
 */
const Text = styled.div`
  ${themes.fonts.FoundersGrotesk}
  font-size: 14px;
  color: ${themes.colors.night};
`;

/**
 * Input drop down, ie "Month" unerlined with down arrow
 */
const InputDropDown = (props: { title: string; value: string | null }) => {
  return (
    <InputContainer>
      {!props.value && <PlaceholderText>{props.title}</PlaceholderText>}
      {props.value && <InputText>{props.value}</InputText>}
      <TriangleDown />
    </InputContainer>
  );
};

const InputContainer = styled.div`
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  border-bottom: 1px solid black;
  cursor: pointer;
`;

const PlaceholderText = styled.div`
  ${themes.fonts.DomaineDisplay};
  color: ${themes.colors.gray};
  font-size: 24px;
  padding: 0 12px 0 0;
`;

const InputText = styled.div`
  font-size: 24px;
  padding-right: 16px;
  color: ${themes.colors.night};
`;

const TriangleDown = styled.div`
  width: 0;
  height: 0;
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid #2f2f2f;
  font-size: 0;
  line-height: 0;
`;

/**
 *
 * Month and Day input
 */
export const DateInput = (props: {
  title: string;
  currentDate: string | null;
  onClick: (e: MouseEvent) => void;
}) => {
  let currentMonth,
    currentDay = null;
  if (props.currentDate) {
    const momentDate = moment(props.currentDate);
    currentMonth = momentDate.format('MMM');
    currentDay = momentDate.format('D');
  }
  return (
    <DateContainer onClick={props.onClick}>
      <Text>{props.title}</Text>
      <MultiInputContainer>
        <InputDropDownContainer>
          <InputDropDown title={'Month'} value={currentMonth || null} />
        </InputDropDownContainer>
        <InputDropDown title={'Day'} value={currentDay} />
      </MultiInputContainer>
    </DateContainer>
  );
};

const InputDropDownContainer = styled.div`
  padding: 0px 16px 0px 0px;
`;

const MultiInputContainer = styled.div`
  width: 100%;
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;

  & + & {
    margin-left: 24px;
  }
`;

const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 *
 * Cycle Length input
 */
export const CycleLengthInput = (props: {
  value: number | null;
  onChangeDays: (days: number) => void;
}) => {
  return (
    <DateContainer>
      <Text>Enter your typical cycle length to forecast your next period</Text>
      <NumberInputContainer>
        <NumberInput
          type="number"
          onChange={e => {
            const newValue = e.target.value ? parseFloat(e.target.value) : 0;
            props.onChangeDays(newValue);
          }}
          value={props.value || ''}
        />
        <NumberInputUnit>days</NumberInputUnit>
      </NumberInputContainer>
    </DateContainer>
  );
};

const NumberInputUnit = styled.div`
  ${themes.fonts.DomaineDisplay}
  font-size: 24px;
  border: 0px;
  border-bottom: 1px solid black;
`;

const NumberInput = styled.input`
  ${themes.fonts.DomaineDisplay}
  font-size: 24px;
  border: 0px;
  border-bottom: 1px solid black;
  width: 50px;
  text-align: left;
`;

const NumberInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 10px 0;
`;

/**
 * Predicted Next Period
 */
export const PredictedNextPeriod = (props: { date: string }) => {
  return (
    <PredictedContainer>
      <LeftBracket color={themes.colors.yellow} />
      <PredictedTextContainer>
        <PredictedText>Predicted next period</PredictedText>
        <PredictedDate>{moment(props.date).format('MMM D')}</PredictedDate>
      </PredictedTextContainer>
      <RightBracket color={themes.colors.yellow} />
    </PredictedContainer>
  );
};

const PredictedContainer = styled.div`
  min-height: 88px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const PredictedTextContainer = styled.div`
  padding: 0 3px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PredictedText = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
  text-align: center;
`;

const PredictedDate = styled.div`
  ${themes.fonts.DomaineDisplay};
  font-size: 24px;
  color: ${themes.colors.KBNight};
`;
